<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: {
    type: [Number, String],
    required: true,
  },
  platform: {
    type: String,
    default: 'instagram',
  },
});

const emit = defineEmits(['select', 'close']);

const store = useStore();
const dialogRef = ref(null);
const isLoading = ref(true);
const posts = ref([]);
const errorMessage = ref('');

const fetchPosts = async () => {
  if (!props.inboxId) return;

  isLoading.value = true;
  errorMessage.value = '';
  try {
    const response = await store.dispatch('commentAutomations/fetchInboxPosts', {
      inboxId: props.inboxId,
      limit: 30,
      platform: props.platform,
    });
    posts.value = response.data || [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      'Unable to fetch posts from Meta. Please make sure the account is properly connected.';
  } finally {
    isLoading.value = false;
  }
};

const handleSelect = post => {
  emit('select', post);
  dialogRef.value?.close();
};

onMounted(() => {
  dialogRef.value?.open();
  fetchPosts();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="platform === 'instagram' ? 'Select Instagram Post or Reel' : 'Select Facebook Page Post'"
    width="3xl"
    @close="emit('close')"
  >
    <div class="min-h-[350px] max-h-[500px] overflow-y-auto p-4">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center h-64 gap-3 text-n-slate-11">
        <Spinner />
        <span class="text-sm">Fetching posts from {{ platform === 'instagram' ? 'Instagram' : 'Facebook' }}...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="flex flex-col items-center justify-center h-64 gap-3 text-center px-4">
        <span class="i-lucide-alert-circle text-3xl text-amber-500" />
        <p class="text-sm text-n-slate-11 max-w-md">{{ errorMessage }}</p>
        <Button label="Try Again" size="sm" variant="faded" @click="fetchPosts" />
      </div>

      <!-- Empty State -->
      <div v-else-if="posts.length === 0" class="flex flex-col items-center justify-center h-64 gap-2 text-center text-n-slate-11">
        <span class="i-lucide-image text-3xl opacity-50" />
        <p class="text-sm">No published posts or reels found for this account.</p>
      </div>

      <!-- Posts Grid -->
      <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <button
          v-for="post in posts"
          :key="post.id"
          type="button"
          class="group relative flex flex-col rounded-xl overflow-hidden border border-n-weak hover:border-n-brand bg-n-surface-2 transition-all text-left shadow-sm hover:shadow-md cursor-pointer"
          @click="handleSelect(post)"
        >
          <!-- Image Container -->
          <div class="aspect-square bg-zinc-900 relative overflow-hidden flex items-center justify-center">
            <img
              v-if="post.thumbnail_url || post.media_url"
              :src="post.thumbnail_url || post.media_url"
              :alt="post.caption || 'Post'"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
            <span v-else class="i-lucide-image text-2xl text-zinc-600" />

            <!-- Media Type Badge -->
            <div
              v-if="post.media_type === 'VIDEO'"
              class="absolute top-2 right-2 bg-black/70 backdrop-blur rounded-full px-1.5 py-0.5 text-[10px] text-white flex items-center gap-1"
            >
              <span class="i-lucide-video text-[10px]" />
              <span>Reel</span>
            </div>
          </div>

          <!-- Caption & Metrics -->
          <div class="p-2.5 flex flex-col justify-between flex-1">
            <p class="text-xs text-n-slate-12 line-clamp-2 leading-relaxed font-normal">
              {{ post.caption || 'No caption' }}
            </p>
            <div class="flex items-center justify-between text-[10px] text-n-slate-10 mt-2">
              <span v-if="post.timestamp">{{ new Date(post.timestamp).toLocaleDateString() }}</span>
              <div class="flex items-center gap-2">
                <span class="flex items-center gap-0.5">
                  <span class="i-lucide-heart text-[10px]" />
                  {{ post.like_count || 0 }}
                </span>
                <span class="flex items-center gap-0.5">
                  <span class="i-lucide-message-circle text-[10px]" />
                  {{ post.comments_count || 0 }}
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  </Dialog>
</template>
